import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import produce from 'immer';
import logger from '../common/logger';
import { CLIENT_CONTEXT_CHANNEL, LEARNING_PLAN_ACTIONS, ROUTER_BASENAME } from '../common/constants';
import {
  chunkArray,
  getCourseAPIHost,
  getEnvironmentConstant,
  getLearningPlanServiceHost,
} from '../common/utilities';
import { isFastTrackLearningPlan, hasContentNotFoundError } from '../common/fastTrackUtils';

export const fetchLearningPlans = createAsyncThunk(
  'userLearningPlans/fetchLearningPlans',
  async ({ env, token }, { rejectWithValue }) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
      let learningPlans = [];
      let offset = null;
      let hasNextPage = true;
      while (hasNextPage) {
        const queryParams = {};
        if (offset) {
          queryParams.offset = offset;
        }
        const url = `${getLearningPlanServiceHost({ env })}/v1/planlist/user`;
        // eslint-disable-next-line
        const response = await axios
          .get(url, {
            headers: {
              ...headers,
            },
            params: queryParams,
          });

        if (response.data.statusCode === 200) {
          if (response.data?.nextUrl) {
            hasNextPage = true;
            offset = response.data.nextUrl;
          } else {
            hasNextPage = false;
            offset = null;
          }
          if (Array.isArray(response.data?.learnPlans) && response.data.learnPlans.length > 0) {
            learningPlans = learningPlans.concat(response.data.learnPlans);
          } else {
            hasNextPage = false;
            offset = null;
          }
        } else {
          return rejectWithValue(response?.data?.message ?? 'Unexpected response');
        }
      }
      return learningPlans;
    } catch (error) {
      logger.error('Error fetching learning plans:', error);
      return rejectWithValue(error.message);
    }
  },
);

export const fetchLearningPlanDetail = createAsyncThunk(
  'userLearningPlans/fetchLearningPlanDetail',
  async ({
    learningPlanId, env, isPreview = false, appVersion, token, isFastTrackOfferingOn = false,
  }, { rejectWithValue }) => {
    try {
      const learningPlanDetail = await (async () => {
        try {
          const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          };
          const url = `${getLearningPlanServiceHost({ env })}/v1/plan/${learningPlanId}/user`;
          const response = await axios
            .get(url, {
              headers: {
                ...headers,
              },
            });
          return response?.data;
        } catch (e) {
          logger.error('Error fetching learning plans', e);
          return rejectWithValue(e);
        }
      })();

      // if fast track offering is on, don't enrich content items here anymore
      // because the service will now do this for us
      const planIsFastTrack = isFastTrackLearningPlan(learningPlanDetail);
      if (!isFastTrackOfferingOn && !planIsFastTrack) {
        const host = getCourseAPIHost({ env, isPreview });
        let courseIds = [];
        if (Array.isArray(learningPlanDetail?.contentItems)) {
          learningPlanDetail.contentItems.forEach((item) => {
            if (item?.id) {
              courseIds.push(item?.id);
            }
          });
        }
        if (courseIds.length) {
          courseIds = [...new Set(courseIds)];
          const chunkedCourseIds = chunkArray(courseIds, 50);
          const contextHeaders = {};
          const contextChannel = getEnvironmentConstant({ key: CLIENT_CONTEXT_CHANNEL, env });
          if (contextChannel) {
            contextHeaders['x-client-context-channel'] = contextChannel;
          }
          const headers = { headers: { 'X-GnosisVersion': appVersion, ...contextHeaders } };
          const courses = await Promise.all(chunkedCourseIds.map(
            (courseIdsChunk) => axios.post(`${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/listCourses/v1/`, courseIdsChunk, headers),
          ))
            .then((arraysOfResults) => {
              const flatResult = [];
              arraysOfResults.reduce((resultArr, result) => {
                if (result?.data && Array.isArray(result.data)) {
                  resultArr.push(...result.data);
                }
                return resultArr;
              }, flatResult);
              return flatResult;
            });
          learningPlanDetail.contentItems = learningPlanDetail.contentItems.map((contentItem) => {
            const courseDetail = courses.find((course) => course.id === contentItem.id);
            return {
              ...contentItem,
              data: courseDetail ?? {},
            };
          }).filter((contentItem) => contentItem !== null);
        }
      }
      return learningPlanDetail;
    } catch (error) {
      logger.error('Error fetching learning plan detail:', error);
      return rejectWithValue(error.message);
    }
  },
);

export const fetchUnassignedLearnings = createAsyncThunk(
  'userLearningPlans/unassignedLearnings',
  async ({
    env, token,
  }, { rejectWithValue }) => {
    try {
      let unassignedLearningPlans = [];
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
      const url = `${getLearningPlanServiceHost({ env })}/v1/planlist/notifications`;
      const response = await axios
        .get(url, {
          headers: {
            ...headers,
          },
        });
      if (response?.status === 200 && Array.isArray(response?.data?.data)) {
        unassignedLearningPlans = response.data.data;
      } else {
        return rejectWithValue(response?.data?.message ?? 'Unexpected response');
      }
      return unassignedLearningPlans;
    } catch (e) {
      logger.error('Error fetching unassigned learning plans: ', e);
      return rejectWithValue(e);
    }
  },
);

export const ackUnassignedLearningPlans = createAsyncThunk(
  'userLearningPlans/ackUnassignedLearningPlans',
  async ({ env, token }, { rejectWithValue, getState }) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
      const unassignedLearningPlans = getState()?.userLearningPlans.unassignedLearningPlans;
      const dataPayload = unassignedLearningPlans.map((learningPlan) => ({
        planId: learningPlan.planId,
        action: LEARNING_PLAN_ACTIONS.ACK_UNASSIGNED,
      }));
      const url = `${getLearningPlanServiceHost({ env })}/v1/planlist/notifications`;
      const response = await axios
        .put(url, dataPayload, {
          headers: {
            ...headers,
          },
        });
      return response;
    } catch (e) {
      logger.error('Error fetching unassigned learning plans: ', e);
      return rejectWithValue(e);
    }
  },
);

const initialState = {
  learningPlans: [],
  learningPlansDetails: {},
  unassignedLearningPlans: [],
  fetchingLearningPlans: false,
  hasFetchedLearningPlans: false,
};

const userLearningPlansSlice = createSlice({
  name: 'userLearningPlans',
  initialState,
  reducers: {
    setLearningPlans: (state, action) => produce(state, (draftState) => {
      draftState.learningPlans = action.payload;
    }),
    clearLearningPlans: (state) => produce(state, (draftState) => {
      draftState.learningPlans = [];
    }),
    updateAckForInactivePlans: (state) => produce(state, (draftState) => {
      draftState.learningPlans = draftState.learningPlans.map((plan) => {
        if (plan.status === 'inactive') {
          return { ...plan, status: 'inactive_acknowledged' };
        }
        return plan;
      });
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchLearningPlans.pending, (state) => {
      const newState = produce(state, (draftState) => {
        draftState.fetchingLearningPlans = true;
      });
      return newState;
    });
    builder.addCase(fetchLearningPlans.fulfilled, (state, action) => {
      const data = action?.payload;
      const newState = produce(state, (draftState) => {
        draftState.fetchingLearningPlans = false;
        draftState.hasFetchedLearningPlans = true;
        draftState.learningPlans = data;
      });
      return newState;
    });
    builder.addCase(fetchLearningPlans.rejected, (state, action) => {
      logger.error('Error in fetchLearningPlans: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.fetchingLearningPlans = false;
        draftState.hasFetchedLearningPlans = false;
      });
      return newState;
    });
    builder.addCase(fetchLearningPlanDetail.pending, (state, action) => {
      const { learningPlanId } = action.meta.arg; // Extract the submitted learningPlanId
      const newState = produce(state, (draftState) => {
        if (!draftState.learningPlansDetails[learningPlanId]) {
          draftState.learningPlansDetails[learningPlanId] = {};
        }
        draftState.learningPlansDetails[learningPlanId].fetching = true;
        draftState.learningPlansDetails[learningPlanId].hasFetched = false;
      });
      return newState;
    });
    builder.addCase(fetchLearningPlanDetail.fulfilled, (state, action) => {
      const { learningPlanId } = action.meta.arg; // Extract the submitted learningPlanId
      const data = action?.payload;
      const newState = produce(state, (draftState) => {
        if (!draftState.learningPlansDetails[learningPlanId]) {
          draftState.learningPlansDetails[learningPlanId] = {};
        }
        draftState.learningPlansDetails[learningPlanId].fetching = false;
        draftState.learningPlansDetails[learningPlanId].hasFetched = true;
        draftState.learningPlansDetails[learningPlanId].data = data;
      });
      return newState;
    });
    builder.addCase(fetchLearningPlanDetail.rejected, (state, action) => {
      const { learningPlanId } = action.meta.arg; // Extract the submitted learningPlanId
      logger.error('Error in fetchLearningPlanDetail: ', action.meta.arg);
      const newState = produce(state, (draftState) => {
        if (!draftState.learningPlansDetails[learningPlanId]) {
          draftState.learningPlansDetails[learningPlanId] = {};
        }
        draftState.learningPlansDetails[learningPlanId].fetching = false;
        draftState.learningPlansDetails[learningPlanId].hasFetched = true;
      });
      return newState;
    });
    builder.addCase(fetchUnassignedLearnings.fulfilled, (state, action) => produce(state, (draftState) => {
      draftState.unassignedLearningPlans = action?.payload || [];
    }));
    builder.addCase(fetchUnassignedLearnings.rejected, (state) => produce(state, (draftState) => {
      draftState.unassignedLearningPlans = [];
    }));
    builder.addCase(ackUnassignedLearningPlans.fulfilled, (state) => produce(state, (draftState) => {
      draftState.unassignedLearningPlans = [];
    }));
  },
});

export const {
  setLearningPlans,
  clearLearningPlans,
  updateAckForInactivePlans,
} = userLearningPlansSlice.actions;

export const getLearningPlans = (state) => state.userLearningPlans.learningPlans;
export const isFetchingLearningPlans = (state) => state.userLearningPlans.fetchingLearningPlans;
export const hasFetchedLearningPlans = (state) => state.userLearningPlans.hasFetchedLearningPlans;
export const getLearningPlanDetail = (state, learningPlanId) => {
  const learningPlansDetails = state.userLearningPlans?.learningPlansDetails;
  if (!learningPlansDetails || !learningPlansDetails[learningPlanId]) {
    return null;
  }
  return learningPlansDetails[learningPlanId].data;
};

export const getHasLearningPlanDetailFetched = (state, learningPlanId) => {
  const learningPlansDetails = state.userLearningPlans?.learningPlansDetails;
  if (!learningPlansDetails || !learningPlansDetails[learningPlanId]) {
    return false;
  }
  return learningPlansDetails[learningPlanId].hasFetched;
};

export const getUnassignedLearningPlans = (state) => state.userLearningPlans.unassignedLearningPlans;

export const hasContentItems = (state, learningPlanId) => {
  const learningPlanDetail = getLearningPlanDetail(state, learningPlanId);
  return Array.isArray(learningPlanDetail?.contentItems) && learningPlanDetail?.contentItems?.length > 0;
};

export const contentItemsCount = (state, learningPlanId) => {
  const learningPlanDetail = getLearningPlanDetail(state, learningPlanId);

  const countNestedContentItems = (items) => {
    if (!Array.isArray(items)) return 0;

    return items.reduce((count, item) => {
      // Skip items that are removed or have content not found errors
      if (item.isRemoved === true || hasContentNotFoundError(item)) {
        return count;
      }

      // If this item has nested contentItems (it's a container/group), don't count it, but count its children (if any)
      if (item.contentItems !== undefined && Array.isArray(item.contentItems)) {
        if (item.contentItems.length > 0) {
          return count + countNestedContentItems(item.contentItems);
        }
        // Container with no children - don't count it
        return count;
      }

      // Count the current item (it's a regular content item)
      return count + 1;
    }, 0);
  };

  return countNestedContentItems(learningPlanDetail?.contentItems);
};
export default userLearningPlansSlice.reducer;
